import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import authAxios from "helpers/authAxios";
import { toast } from "react-toastify";
import { APP_VERSION } from "../app_config";
import { setCookie, getCookie, removeCookie } from "helpers/cookie";

export const login = createAsyncThunk("Auth/login", async (data, { rejectWithValue }) => {
  try {
    const response = await authAxios.post("/login", data);
    return response.data;
  } catch (error) {
    const body = error.response?.data;
    const message =
      (typeof body === "object" ? body?.error || body?.message : body) ||
      error.message;
    return rejectWithValue(message);
  }
});

export const loginWithGoogle = createAsyncThunk(
  "Auth/loginWithGoogle",
  async (data, { rejectWithValue }) => {
    try {
      const token = data.access_token;
      const requestOptions = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          access_token: `${token}`
        },
        body: JSON.stringify(data)
      };

      const url = `${process.env.REACT_APP_API_URL}/auth/google_oauth2/callback`;
      const response = await axios.post(url, requestOptions);
      localStorage.setItem("app_version", APP_VERSION);

      return response.data;
    } catch (error) {
      // Same shape as `login` above. The old version threw a bare Error built
      // from `error.message`, which is the axios text ("Request failed with
      // status code 422") and never the server's explanation — so the reason a
      // sign-in was refused could not reach the screen even once the caller
      // started unwrapping the rejection.
      const body = error.response?.data;
      const message =
        (typeof body === "object" ? body?.error || body?.message : body) ||
        error.message ||
        "An unknown error occurred when signing in with Google.";
      return rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk("Auth/logout", async () => {
  try {
    const token =
      getCookie("access_token") || localStorage.getItem("access_token");

    if (token !== null) {
      const response = await authAxios.delete("/logout", {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("token_expiration");

      removeCookie("access_token");
      removeCookie("refresh_token");
      removeCookie("token_expiration");

      return response.data;
    }

    return null;
  } catch (error) {
    throw new Error(error.message);
  }
});

export const verifyToken = createAsyncThunk("Auth/verifyToken", async token => {
  try {
    const response = await authAxios.post("/login", token);
    return response.data;
  } catch (error) {
    throw new Error(error.message);
  }
});

export const verifyRecaptcha = createAsyncThunk(
  "Auth/verifyRecaptcha",
  async token => {
    try {
      const response = await authAxios.post("/verify_recaptcha", { token });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

const handleAuthSuccess = (state, action) => {
  state.loading = false;
  const { data, access_token, token_expiration, refresh_token } =
    action.payload;

  if (data) {
    state.currentUser = data;

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("token_expiration", token_expiration);
    localStorage.setItem("refresh_token", refresh_token);

    setCookie("access_token", access_token, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });
    setCookie("refresh_token", refresh_token, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });
    setCookie("token_expiration", token_expiration, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });

    toast.success(`Logged in as ${data.email}`);
  } else {
    toast.error("Sign in was failed.");
  }
};

// NOTE: this never matches. Every thunk in this slice is registered under
// "Auth/" with a capital A, and startsWith is case-sensitive — so no rejection
// here has ever reset `loading` or raised its toast. Left as-is deliberately:
// correcting the casing would switch on error toasts across the whole auth
// slice at once, including pages that already surface their own errors, which
// is a change worth making on purpose rather than as a side effect.
const isRejectedAction = action => action.type.startsWith("auth/") && action.type.endsWith("/rejected");
export const authSlice = createSlice({
  name: "auth",
  initialState: {
    currentUser: {},
    loading: false
  },
  reducers: {
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        state.loading = true;
      })
      .addCase(login.fulfilled, handleAuthSuccess)
      .addCase(verifyToken.pending, state => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, handleAuthSuccess)
      .addCase(logout.fulfilled, state => {
        state.loading = false;
        state.currentUser = {};
        localStorage.clear();

        removeCookie("access_token");
        removeCookie("refresh_token");
        removeCookie("token_expiration");

        console.log("localStorage cleared");
      })
      .addCase(loginWithGoogle.fulfilled, handleAuthSuccess)
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        toast.error(action.error.message);
      });
  }
});

export const { setCurrentUser } = authSlice.actions;

export default authSlice.reducer;
